import React, { useEffect, useState } from 'react'

import { EnvelopeIcon, HomeIcon, UserCircleIcon } from '@heroicons/react/24/outline'
import { PhoneIcon } from '@heroicons/react/24/outline'
import { Field, Form, Formik } from 'formik'
import Select from 'react-select'

import CallPhone from '@/components/CallPhone'
import { InputField } from '@/components/FormikInputs'
import Spinner from '@/components/Spinner'
import useCardsCollapseStatus from '@/features/FileContacts/useCardsCollapseStatus'
import { Application } from '@/graphql/generated'
import {
  useGetDealTeamsByApplicationQuery,
  useUpsertDealTeamMutation,
} from '@/services/application'
import { useGetLoggedUserQuery, useGetStaffUsersQuery } from '@/services/user'
import { useGetAllUsersDataQuery, useLazyGetUserDataQuery } from '@/services/userData'
import { DealTeam } from '@/types/global'
import { PaginatedResults, User } from '@/types/unlock'
import {
  assignDealTeamDisabled,
  getUserFullNameOrEmail,
  isAllowedToAssignSales,
} from '@/utils/user'

interface DealTeamCardProps {
  application: Application
  shouldBeLocked: boolean
}

export function DealTeamContacts({ application, shouldBeLocked }: DealTeamCardProps) {
  const [salesContactDetails, setSalesContactDetails] = useState<User>(null)
  const [processingContactDetails, setProcessingContactDetails] = useState<User>(null)
  const [postClosingContactDetails, setPostClosingContactDetails] = useState<User>(null)
  const [closingContactDetails, setClosingContactDetails] = useState<User>(null)
  const [valuationsContactDetails, setValuationsContactDetails] = useState<User>(null)
  const [underwritingContactDetails, setUnderwritingContactDetails] = useState<User>(null)
  const [uamContactDetails, setUamContactDetails] = useState<User>(null)
  const { data: dealTeams, isLoading: isLoadingDealTeams } = useGetDealTeamsByApplicationQuery(
    application?.id,
    { skip: !application?.id }
  )
  const [getUserById] = useLazyGetUserDataQuery()
  const { data: staffUsers } = useGetStaffUsersQuery()
  const [upsertDealTeam] = useUpsertDealTeamMutation()
  const { data: user } = useGetLoggedUserQuery()

  const isAssignDisabled = shouldBeLocked || assignDealTeamDisabled(user, application)

  useEffect(() => {
    async function getDealTeams() {
      const { results } = dealTeams
      const salesContact = results.find((team) => team?.type === 'sales')
      const processingContact = results.find((team) => team?.type === 'processing')
      const closingContact = results.find((team) => team?.type === 'closing')
      const valuationsContact = results.find((team) => team?.type === 'valuations')
      const underwritingContact = results.find((team) => team?.type === 'underwriting')
      const postClosingContact = results.find((team) => team?.type === 'post-closing')
      const uamContact = results.find((team) => team?.type === 'uam')

      if (salesContact) {
        const resp = await getUserById(salesContact.user, true).unwrap()
        setSalesContactDetails(resp)
      }

      if (processingContact) {
        const resp = await getUserById(processingContact.user, true).unwrap()
        setProcessingContactDetails(resp)
      }

      if (postClosingContact) {
        const resp = await getUserById(postClosingContact.user, true).unwrap()
        setPostClosingContactDetails(resp)
      }

      if (closingContact) {
        const resp = await getUserById(closingContact.user, true).unwrap()
        setClosingContactDetails(resp)
      }

      if (valuationsContact) {
        const resp = await getUserById(valuationsContact.user, true).unwrap()
        setValuationsContactDetails(resp)
      }

      if (underwritingContact) {
        const resp = await getUserById(underwritingContact.user, true).unwrap()
        setUnderwritingContactDetails(resp)
      }

      if (uamContact) {
        const resp = await getUserById(uamContact.user, true).unwrap()
        setUamContactDetails(resp)
      }
    }

    if (dealTeams?.results.length > 0) {
      getDealTeams()
    }
  }, [dealTeams])

  const updateDealTeamContact = (
    dealTeams: PaginatedResults<DealTeam[]>,
    application: Application,
    type: string
  ) => {
    return (user) => {
      upsertDealTeam({
        id: dealTeams?.results.find((team) => team?.type === type)?.id,
        user: user.userId,
        type: type,
        application: application.id,
      })
    }
  }

  return (
    <div className='flex flex-col gap-2'>
      {isLoadingDealTeams && <Spinner size={4} />}
      <DealTeamCard
        label='Unlock Sales'
        contact={salesContactDetails}
        applicationName={application?.name}
        staffUsers={staffUsers}
        handleUserUpdate={updateDealTeamContact(dealTeams, application, 'sales')}
        shouldBeLocked={shouldBeLocked || !isAllowedToAssignSales(user, application)}
      />
      <DealTeamCard
        label='Unlock Processing'
        contact={processingContactDetails}
        applicationName={application?.name}
        staffUsers={staffUsers}
        handleUserUpdate={updateDealTeamContact(dealTeams, application, 'processing')}
        shouldBeLocked={isAssignDisabled}
      />
      <DealTeamCard
        label='Unlock Valuations'
        contact={valuationsContactDetails}
        applicationName={application?.name}
        staffUsers={staffUsers}
        handleUserUpdate={updateDealTeamContact(dealTeams, application, 'valuations')}
        shouldBeLocked={isAssignDisabled}
      />
      <DealTeamCard
        label='Unlock Underwriting'
        contact={underwritingContactDetails}
        applicationName={application?.name}
        staffUsers={staffUsers}
        handleUserUpdate={updateDealTeamContact(dealTeams, application, 'underwriting')}
        shouldBeLocked={isAssignDisabled}
      />
      <DealTeamCard
        label='Unlock UAM'
        contact={uamContactDetails}
        applicationName={application?.name}
        staffUsers={staffUsers}
        handleUserUpdate={updateDealTeamContact(dealTeams, application, 'uam')}
        shouldBeLocked={isAssignDisabled}
      />

      <DealTeamCard
        label='Unlock Closing'
        contact={closingContactDetails}
        applicationName={application?.name}
        staffUsers={staffUsers}
        handleUserUpdate={updateDealTeamContact(dealTeams, application, 'closing')}
        shouldBeLocked={isAssignDisabled}
      />
      <DealTeamCard
        label='Unlock Post Closing'
        contact={postClosingContactDetails}
        applicationName={application?.name}
        staffUsers={staffUsers}
        handleUserUpdate={updateDealTeamContact(dealTeams, application, 'post-closing')}
        shouldBeLocked={isAssignDisabled}
      />
    </div>
  )
}

function DealTeamCard({
  label,
  applicationName,
  contact,
  staffUsers = [],
  handleUserUpdate,
  shouldBeLocked,
}) {
  const { isExpanded, toggleIsExpanded } = useCardsCollapseStatus(false)
  const {
    data: users,
    isLoading: isLoadingUsers,
    isSuccess: isSuccessLoadingUsers,
  } = useGetAllUsersDataQuery(true)
  const manager = users?.find((u) => u.id === contact?.data?.manager)

  const getLabel = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name} - (${user.username})`
    }
    return user.username
  }

  const handleSendEmail = (contact) => {
    window.location.href = `mailto:${contact?.email}?subject=${label} Inquiry - ${applicationName}`
  }

  return (
    <Formik
      initialValues={{
        userId: null,
        contact_name: contact?.data?.full_name || '',
        phone: contact?.data?.phone || '',
        email: contact?.email || '',
        address: contact?.data?.staff_address || '',
      }}
      onSubmit={(values) => {
        handleUserUpdate(values)
        toggleIsExpanded()
      }}
      enableReinitialize
    >
      {({ submitForm, setFieldValue, values }) => (
        <Form>
          <div className='w-full card bg-base-100 shadow-md flex flex-col border'>
            <div
              className={`w-full flex justify-between px-5 py-2 ${
                isExpanded && 'bg-[#24d3ee]'
              } transition-colors duration-500 rounded-t-box  cursor-pointer`}
              onClick={() => toggleIsExpanded()}
            >
              <div className='flex flex-col text-start'>
                <p className='text-base font-bold'>{label}</p>
              </div>
              <div className='flex items-center gap-2'>
                {!isExpanded && (
                  <>
                    <PhoneIcon className='w-4 h-4 cursor-not-allowed select-none' />
                    {contact && (
                      <div
                        className='tooltip tooltip-left'
                        data-tip={`E-mail: ${values.email}`}
                      >
                        <EnvelopeIcon
                          className={`w-4 ${contact ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                          onClick={(e) => {
                            if (contact) {
                              e.stopPropagation()
                              handleSendEmail(contact)
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div
              className={`px-5 ${(contact || isExpanded) && 'pb-2'} pt-0 flex flex-col gap-y-2 ${
                isExpanded && 'pt-3'
              }`}
            >
              {isExpanded && (
                <div
                  className={label === 'Unlock Sales' && shouldBeLocked ? 'tooltip' : ''}
                  data-tip={
                    label === 'Unlock Sales' && shouldBeLocked
                      ? 'You do not have permission to assign a Sales user.'
                      : ''
                  }
                >
                  <Field
                    name='userId'
                    component={Select}
                    options={staffUsers?.map((user) => ({
                      value: user.id,
                      label: getLabel(user),
                    }))}
                    placeholder={
                      contact ? `Change the ${label} contact` : `Select an ${label} contact`
                    }
                    onChange={({ value }) => {
                      setFieldValue('userId', value)
                      submitForm()
                    }}
                    isDisabled={shouldBeLocked}
                  />
                </div>
              )}
              {contact && (
                <div className='flex flex-row gap-1 text-xs'>
                  <UserCircleIcon className='w-4' />
                  <InputField
                    name='contact_name'
                    placeholder='Contact name'
                    parentClassName='w-full'
                    className='cursor-default test-class'
                    disabled
                  />
                </div>
              )}
              {isExpanded && contact && (
                <>
                  <div className='flex flex-row gap-1 text-xs items-center'>
                    <CallPhone callTo={values.phone} />
                    <InputField
                      name='phone'
                      placeholder='Phone'
                      parentClassName='w-full'
                      className='cursor-default test-class'
                      disabled
                    />
                  </div>
                  <div className='flex flex-row gap-1 text-xs items-center'>
                    <a
                      href={`mailto:${contact?.email}?subject=${label} Inquiry - ${applicationName}`}
                      className='flex'
                    >
                      <EnvelopeIcon className='w-4 cursor-pointer' />
                    </a>
                    <InputField
                      name='email'
                      placeholder='E-mail'
                      parentClassName='w-full'
                      className='cursor-default test-class'
                      disabled
                    />
                  </div>
                  <div className='flex flex-row gap-1 text-xs'>
                    <HomeIcon className='w-4' />
                    <InputField
                      name='address'
                      placeholder='Address'
                      parentClassName='w-full'
                      disabled
                    />
                  </div>
                  <div className='mb-3'>
                    {isLoadingUsers && <div className='skeleton h-4 w-full' />}
                    {isSuccessLoadingUsers && (
                      <div className='flex items-center gap-1 font-semibold'>
                        <UserCircleIcon
                          height={16}
                          width={16}
                        />
                        <span>Manager:</span>
                        {manager ? (
                          <span>{getUserFullNameOrEmail(manager)}</span>
                        ) : (
                          <span className='text-gray-400'>Not assigned</span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}
